import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, HostBinding, Input, Output, ViewChild, inject } from '@angular/core';
import { MatMenu } from '@angular/material/menu';
import { Shell } from '@tytapp/common';
import { ContentBlock } from '@tytapp/content/content-block';
import { ContentBlockType } from '@tytapp/content/content-block-type';
import { v4 as uuid } from 'uuid';
import { ContentBlockPickerComponent } from '../content-block-picker/content-block-picker.component';
import { ContentBlocksService } from '../content-blocks.service';
import { Subject } from 'rxjs';
import { isClientSide } from '@tytapp/environment-utils';

@Component({
    selector: 'tyt-content-editor',
    templateUrl: './content-editor.component.html',
    styleUrls: ['./content-editor.component.scss']
})
export class ContentEditorComponent {
    private blockTypes = inject(ContentBlocksService);
    private shell = inject(Shell);

    ngOnInit() {
        if (isClientSide()) {
            document.addEventListener('keydown', this.keyDownListener);
            document.addEventListener('keyup', this.keyUpListener);
        }
    }

    ngOnDestroy() {
        if (isClientSide()) {
            document.removeEventListener('keydown', this.keyDownListener);
            document.removeEventListener('keyup', this.keyUpListener);
        }
    }

    @Input() preferredTypes: string[];
    @Input() locked: boolean;
    @Input() enableNotices = true;
    @Input() blocks: ContentBlock[];
    @Output() autoSave = new Subject<void>();
    @Output() addMenu = new EventEmitter<MatMenu>();

    @Input() @HostBinding('attr.data-theme')
    theme: 'dark' | 'light';

    keyDownListener = (ev: KeyboardEvent) => {
        if (ev.target instanceof HTMLInputElement || ev.target instanceof HTMLTextAreaElement || ev.target instanceof HTMLButtonElement || ev.target instanceof HTMLAnchorElement)
            return;

        if (ev.key === 'Control')
            this.ctrlDown = true;
        else if (ev.key === 'Shift')
            this.shiftDown = true;
        else if (ev.key === 'Alt')
            this.altDown = true;
        else
            return;

        ev.preventDefault();
    }

    keyUpListener = (ev: KeyboardEvent) => {
        if (ev.target instanceof HTMLInputElement || ev.target instanceof HTMLTextAreaElement || ev.target instanceof HTMLButtonElement || ev.target instanceof HTMLAnchorElement)
            return;

        if (ev.key === 'Control')
            this.ctrlDown = false;
        else if (ev.key === 'Shift')
            this.shiftDown = false;
        else if (ev.key === 'Alt')
            this.altDown = false;
        else
            return;

        ev.preventDefault();
    }

    @Input()
    editorMode: 'normal' | 'reorder' | 'preview' = 'normal';
    addBefore: ContentBlock;
    addAsSecondary: boolean;
    ctrlDown = false;
    shiftDown = false;
    altDown = false;

    get primaryBlocks() { return this.blocks?.filter(x => !x.secondary) ?? []; }
    get secondaryBlocks() { return this.blocks?.filter(x => x.secondary) ?? []; }
    get isMultiColumn() { return this.secondaryBlocks.length > 0; }

    @HostBinding('class.has-multi-column')
    get shouldUseTwoColumn() {
        return this.isMultiColumn && this.editorMode !== 'reorder';
    }

    @ViewChild('addMenu', { static: true })
    set _addMenu(menu: MatMenu) {
        this.addMenu?.emit(menu);
    }

    get quickBlockTypes() {
        if (this.preferredTypes) {
            return this.preferredTypes.map(id => this.allBlockTypes().find(t => t.id === id)).filter(x => x);
        }

        return this.allBlockTypes().slice(0, 5);
    }

    allBlockTypes() {
        return this.blockTypes.all();
    }

    blockIdentity(index: number, block: ContentBlock) {
        return block.id || block;
    }

    addBlockBefore(existingBlock: ContentBlock, newType: ContentBlockType) {
        this.addBefore = existingBlock;
        this.addBlock(newType, { secondary: existingBlock.secondary });
    }

    onClickAddBeforeLast(secondary = false) {
        this.addBefore = undefined;
        this.addAsSecondary = secondary;
    }

    blockType(block: ContentBlock) {
        return this.blockTypes.getFromBlock(block);
    }

    blockTypeId(block: ContentBlock) {
        return block.type;
    }

    addBlock(type: ContentBlockType, options: Partial<ContentBlock> = {}) {
        const block = JSON.parse(JSON.stringify(<ContentBlock>{
            ...(type.template ?? {}),
            type: type.id,
            id: uuid(),
            secondary: this.addAsSecondary,
            __initialEditorMode: 'edit',
            ...options
        }));

        let beforeIndex = this.blocks.indexOf(this.addBefore);

        if (beforeIndex < 0) {
            this.blocks.push(block);
        } else {
            this.blocks.splice(beforeIndex, 0, block);
        }
    }

    removeBlock(block: ContentBlock) {
        if (!this.shiftDown) {
            if (!confirm("Are you sure you want to remove this block?"))
                return;
        }

        let index = this.blocks.indexOf(block);

        if (index >= 0)
            this.blocks.splice(index, 1);
    }

    delayAutoSave() {
        this.autoSave.next();
    }

    drop(event: CdkDragDrop<string[]>) {
        moveItemInArray(this.blocks, event.previousIndex, event.currentIndex);
    }

    showPicker() {
        this.shell.showDialog(ContentBlockPickerComponent, block => this.addBlock(block));
    }
}
